import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ArrowRight } from "lucide-react";

const FIELDS = [
  { key: "cms", label: "What platform is your website on?", ph: "e.g. Shopify, WordPress, Webflow, custom" },
  { key: "hosting", label: "Where is it hosted?", ph: "e.g. Shopify, Vercel, AWS, unsure" },
  { key: "payment_provider", label: "Payment provider", ph: "e.g. Stripe, PayPal, none" },
  { key: "crm", label: "CRM (if any)", ph: "e.g. HubSpot, Salesforce, none" },
  { key: "products_services", label: "What do you sell?", ph: "Briefly describe your main products or services", area: true },
  { key: "pricing", label: "How is pricing handled?", ph: "e.g. fixed prices, quotes, subscriptions" },
  { key: "fulfillment", label: "How do customers buy / book / order today?", ph: "Describe the current buying journey", area: true },
  { key: "authorization", label: "What may we change on your behalf?", ph: "e.g. add schema, publish llms.txt, add API endpoints", area: true },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, loading } = useAuth();
  const [org, setOrg] = useState(null);
  const [plan, setPlan] = useState(null);
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    const orgId = params.get("org") || user.organization_id;
    if (!orgId) { navigate("/dashboard"); return; }
    api.get(`/onboarding/${orgId}`).then(({ data }) => {
      setOrg(data.organization); setPlan(data.plan); setResponses(data.responses || {});
    }).catch(() => navigate("/dashboard"));
  }, [user, loading, params, navigate]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/onboarding", { organization_id: org.id, responses });
      toast.success("Onboarding complete — our agents are starting implementation.");
      navigate("/dashboard");
    } catch { toast.error("Could not submit. Please retry."); }
    finally { setSubmitting(false); }
  };

  if (!org) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="flex items-center gap-2 text-emerald-600 mb-2"><ShieldCheck className="h-4 w-4" /><span className="text-xs font-mono uppercase tracking-wide">No-touch onboarding · {plan?.name}</span></div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Tell us about {org.display_name}</h1>
        <p className="text-slate-500 mt-2">A few questions so our agents can begin authorized implementation. No call, no meeting. We never ask for plain-text passwords — connect systems via OAuth later.</p>

        <div className="mt-8 space-y-5">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <Label className="text-slate-700 font-medium">{f.label}</Label>
              {f.area ? (
                <Textarea data-testid={`onb-${f.key}`} placeholder={f.ph} value={responses[f.key] || ""} onChange={(e) => setResponses({ ...responses, [f.key]: e.target.value })} className="mt-1.5" rows={3} />
              ) : (
                <Input data-testid={`onb-${f.key}`} placeholder={f.ph} value={responses[f.key] || ""} onChange={(e) => setResponses({ ...responses, [f.key]: e.target.value })} className="mt-1.5 h-12" />
              )}
            </div>
          ))}
        </div>

        <Button onClick={submit} disabled={submitting} data-testid="onb-submit" className="w-full h-13 py-6 mt-8 rounded-full bg-indigo-600 hover:bg-indigo-700 font-semibold text-base">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Submit & Begin Implementation <ArrowRight className="h-4 w-4 ml-2" /></>}
        </Button>
      </div>
    </div>
  );
}
